import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

export interface GroupedSelectItem {
  value: string
  label: string
  icon?: string
}

export interface GroupedSelectGroup {
  label: string
  items: GroupedSelectItem[]
  collapsed?: boolean
}

@customElement('grouped-select')
export class GroupedSelect extends LitElement {
  static override styles = css`
    :host {
      display: block;
      position: relative;
    }

    .select-trigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: transparent;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: 8px;
      color: var(--cairn-text-color, #1e293b);
      font-size: 13px;
      padding: 8px 12px;
      cursor: pointer;
      width: 100%;
      box-sizing: border-box;
      transition: border-color 0.15s ease;
    }

    .select-trigger:hover {
      border-color: var(--cairn-text-secondary-color, #64748b);
    }

    .select-trigger.open {
      border-color: var(--cairn-primary-color, #1976d2);
    }

    .trigger-content {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }

    .trigger-icon {
      font-family: 'Material Symbols Outlined';
      font-size: 18px;
      flex-shrink: 0;
    }

    .trigger-label {
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 11px;
      position: absolute;
      top: -6px;
      left: 10px;
      background: var(--cairn-surface-color, #ffffff);
      padding: 0 4px;
      font-weight: 500;
    }

    .trigger-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .trigger-placeholder {
      color: var(--cairn-text-muted-color, #94a3b8);
    }

    .chevron {
      font-family: 'Material Symbols Outlined';
      font-size: 18px;
      color: var(--cairn-text-secondary-color, #64748b);
      flex-shrink: 0;
      transition: transform 0.2s ease;
    }

    .chevron.open {
      transform: rotate(180deg);
    }

    .dropdown-panel {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      background: var(--cairn-surface-color, #ffffff);
      border-radius: 8px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      max-height: 240px;
      overflow-y: auto;
      z-index: 100;
      padding: 4px 0;
    }

    .group-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 6px 12px;
      cursor: pointer;
      user-select: none;
      transition: background 0.1s ease;
    }

    .group-header:hover {
      background: var(--cairn-background-color, #f1f5f9);
    }

    .group-label {
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .group-chevron {
      font-family: 'Material Symbols Outlined';
      font-size: 16px;
      color: var(--cairn-text-muted-color, #94a3b8);
      transition: transform 0.2s ease;
    }

    .group-chevron.collapsed {
      transform: rotate(-90deg);
    }

    .option {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 12px 8px 16px;
      cursor: pointer;
      transition: background 0.1s ease;
      color: var(--cairn-text-color, #1e293b);
      font-size: 13px;
    }

    .option:hover {
      background: var(--cairn-background-color, #f1f5f9);
    }

    .option.selected {
      background: var(--cairn-border-color, #e2e8f0);
      color: var(--cairn-text-color, #1e293b);
    }

    .option-icon {
      font-family: 'Material Symbols Outlined';
      font-size: 18px;
      width: 24px;
      text-align: center;
      flex-shrink: 0;
    }

    .option-placeholder-icon {
      width: 24px;
      text-align: center;
      flex-shrink: 0;
      color: var(--cairn-text-muted-color, #94a3b8);
      font-size: 14px;
    }

    .ungrouped-divider {
      height: 1px;
      background: var(--cairn-background-color, #f1f5f9);
      margin: 4px 0;
    }

    .select-trigger input.trigger-input {
      background: transparent;
      border: none;
      outline: none;
      color: var(--cairn-text-color, #1e293b);
      font-size: 13px;
      width: 100%;
      min-width: 0;
      padding: 0;
    }

    .select-trigger input.trigger-input::placeholder {
      color: var(--cairn-text-muted-color, #94a3b8);
    }

    .no-results {
      padding: 12px 16px;
      color: var(--cairn-text-muted-color, #94a3b8);
      font-size: 13px;
      text-align: center;
    }
  `

  @property({ type: String })
  label = ''

  @property({ type: String })
  value = ''

  @property({ type: String })
  placeholder = 'Select...'

  @property({ type: Boolean })
  searchable = false

  @property({ type: Array })
  groups: GroupedSelectGroup[] = []

  @property({ type: Array })
  ungroupedItems: GroupedSelectItem[] = []

  @state()
  private open = false

  @state()
  private collapsedGroups: Set<string> = new Set()

  @state()
  private searchQuery = ''

  override connectedCallback(): void {
    super.connectedCallback()
    this._outsideClickHandler = this._handleOutsideClick.bind(this)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    document.removeEventListener('click', this._outsideClickHandler!, true)
  }

  private _outsideClickHandler?: (e: MouseEvent) => void

  private _handleOutsideClick(e: MouseEvent): void {
    const path = e.composedPath()
    if (!path.includes(this)) {
      this.open = false
      document.removeEventListener('click', this._outsideClickHandler!, true)
    }
  }

  private toggleOpen(): void {
    this.open = !this.open
    if (this.open) {
      document.addEventListener('click', this._outsideClickHandler!, true)
      if (this.searchable) {
        this.searchQuery = ''
        this.updateComplete.then(() => {
          this.shadowRoot?.querySelector<HTMLInputElement>('.trigger-input')?.focus()
        })
      }
    } else {
      document.removeEventListener('click', this._outsideClickHandler!, true)
      this.searchQuery = ''
    }
  }

  private toggleGroup(groupLabel: string): void {
    const next = new Set(this.collapsedGroups)
    if (next.has(groupLabel)) {
      next.delete(groupLabel)
    } else {
      next.add(groupLabel)
    }
    this.collapsedGroups = next
  }

  private getFilteredItems(): GroupedSelectItem[] {
    const q = this.searchQuery.toLowerCase()
    const results: GroupedSelectItem[] = []
    for (const item of this.ungroupedItems) {
      if (item.label.toLowerCase().includes(q)) results.push(item)
    }
    for (const group of this.groups) {
      for (const item of group.items) {
        if (item.label.toLowerCase().includes(q)) results.push(item)
      }
    }
    return results
  }

  private selectItem(item: GroupedSelectItem): void {
    this.value = item.value
    this.open = false
    document.removeEventListener('click', this._outsideClickHandler!, true)
    this.dispatchEvent(
      new CustomEvent('value-changed', {
        detail: { value: item.value, label: item.label, icon: item.icon },
        bubbles: true,
      })
    )
  }

  private getSelectedItem(): GroupedSelectItem | undefined {
    for (const item of this.ungroupedItems) {
      if (item.value === this.value) {
        return item
      }
    }
    for (const group of this.groups) {
      for (const item of group.items) {
        if (item.value === this.value) {
          return item
        }
      }
    }
    return undefined
  }

  private renderItem(item: GroupedSelectItem): unknown {
    return html`
      <div
        class="option ${item.value === this.value ? 'selected' : ''}"
        @click=${() => this.selectItem(item)}
      >
        ${item.icon
          ? html`<span class="option-icon">${item.icon}</span>`
          : html`<span class="option-placeholder-icon">—</span>`}
        <span>${item.label}</span>
      </div>
    `
  }

  private renderFilteredItems(): unknown {
    const items = this.getFilteredItems()
    if (items.length === 0) {
      return html`<div class="no-results">No matches</div>`
    }
    return items.map(item => this.renderItem(item))
  }

  private renderGroupedItems(): unknown {
    return html`
      ${this.ungroupedItems.map(item => this.renderItem(item))}
      ${this.ungroupedItems.length && this.groups.length
        ? html`<div class="ungrouped-divider"></div>`
        : ''}
      ${this.groups.map(group => {
        const isCollapsed = this.collapsedGroups.has(group.label)
        return html`
          <div class="group-header" @click=${() => this.toggleGroup(group.label)}>
            <span class="group-label">${group.label}</span>
            <span class="group-chevron ${isCollapsed ? 'collapsed' : ''}">expand_more</span>
          </div>
          ${!isCollapsed ? group.items.map(item => this.renderItem(item)) : ''}
        `
      })}
    `
  }

  override render(): unknown {
    const selected = this.getSelectedItem()
    const showInput = this.searchable && this.open

    return html`
      <div>
        ${this.label ? html`<span class="trigger-label">${this.label}</span>` : ''}
        <button class="select-trigger ${this.open ? 'open' : ''}" @click=${this.toggleOpen}>
          <div class="trigger-content">
            ${showInput
              ? html`<input
                  class="trigger-input"
                  type="text"
                  placeholder=${selected ? selected.label : 'Search...'}
                  .value=${this.searchQuery}
                  @input=${(e: Event) => {
                    this.searchQuery = (e.target as HTMLInputElement).value
                  }}
                  @click=${(e: Event) => e.stopPropagation()}
                />`
              : html`
                  ${selected?.icon ? html`<span class="trigger-icon">${selected.icon}</span>` : ''}
                  ${selected
                    ? html`<span class="trigger-text">${selected.label}</span>`
                    : html`<span class="trigger-text trigger-placeholder"
                        >${this.placeholder}</span
                      >`}
                `}
          </div>
          <span class="chevron ${this.open ? 'open' : ''}">expand_more</span>
        </button>

        ${this.open
          ? html`
              <div class="dropdown-panel">
                ${this.searchQuery ? this.renderFilteredItems() : this.renderGroupedItems()}
              </div>
            `
          : ''}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'grouped-select': GroupedSelect
  }
}
